<!-- 
DropdownWithOverlay Component

A flexible dropdown with backdrop overlay support. Combines DaisyUI dropdown with custom overlay effects.

Usage:
<DropdownWithOverlay v-model="isOpen" position="dropdown-start">
  <template #trigger>
    <button>Open Menu</button>
  </template>
  <template #content="{ close }">
    <div>Your dropdown content here</div>
    <button @click="close">Close</button>
  </template>
</DropdownWithOverlay>

Key Features:
- v-model support for open/close state
- Backdrop overlay with customizable gradient
- Smooth transitions and animations
- Flexible positioning (start/end/top/bottom/left/right)
- Exposed methods: close() and toggle() via ref

Props:
- modelValue: Control open/close state (v-model)
- position: Dropdown position ('dropdown-start', 'dropdown-end', etc.)
- keepOpen: Keep dropdown open when clicking inside content
- closeOnOverlayClick: Close when clicking overlay backdrop
- contentClass: Custom classes for dropdown content
- overlayClass: Custom classes for backdrop overlay
-->
<template>
  <!-- Backdrop overlay with gradient effect -->
  <Transition
    :enter-active-class="`transition-opacity ${transitionDuration}`"
    :leave-active-class="`transition-opacity ${transitionDuration}`"
    enter-from-class="opacity-0"
    leave-to-class="opacity-0"
    @before-leave="isAnimating = true"
    @after-leave="isAnimating = false"
  >
    <div 
      v-if="modelValue"
      :class="[
        'fixed inset-0',
        overlayClass
      ]"
      :style="{ zIndex: overlayZIndex }"
      @click="handleOverlayClick"
    ></div>
  </Transition>

  <!-- Dropdown container -->
  <div 
    ref="dropdownRef"
    :class="[
      'dropdown',
      dropdownClass,
      position,
      { 'dropdown-open': modelValue },
      { 'relative': makeRelative }
    ]"
    :style="modelValue || isAnimating ? { zIndex: dropdownZIndex } : {}"
  >
    <!-- Trigger slot - the button/element that opens the dropdown -->
    <div 
      tabindex="0"
      role="button"
      @click="toggleDropdown"
    >
      <slot name="trigger" :isOpen="modelValue" :toggle="toggleDropdown">
        <!-- Default trigger if none provided -->
        <button class="btn btn-ghost btn-sm">
          Open Dropdown
        </button>
      </slot>
    </div>

    <!-- Content slot - the dropdown content -->
    <div 
      v-if="modelValue"
      :tabindex="keepOpen ? '-1' : '0'"
      :class="[
        'dropdown-content',
        contentClass
      ]"
      :style="{ zIndex: contentZIndex }"
    >
      <slot name="content" :close="closeDropdown">
        <!-- Default content if none provided -->
        <div class="bg-base-100 rounded-box p-4 shadow-lg border border-base-300">
          Dropdown content goes here
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  
  position: {
    type: String,
    default: 'dropdown-end',
    validator: (value) => [
      'dropdown-end',
      'dropdown-start', 
      'dropdown-top',
      'dropdown-bottom',
      'dropdown-left',
      'dropdown-right'
    ].includes(value)
  },
  
  keepOpen: {
    type: Boolean,
    default: true  // Prevents closing when clicking inside content
  },
  
  closeOnOverlayClick: {
    type: Boolean,
    default: true
  },
  
  dropdownClass: {
    type: String,
    default: ''
  },
  
  contentClass: {
    type: String,
    default: 'bg-base-100 rounded-box p-3 shadow-lg border border-base-300'
  },
  
  overlayClass: {
    type: String,
    default: 'bg-gradient-to-l from-black/10 to-transparent backdrop-blur-sm'
  },
  
  transitionDuration: {
    type: String,
    default: 'duration-500'
  },
  
  // Z-index layering
  overlayZIndex: {
    type: Number,
    default: 49
  },
  
  dropdownZIndex: {
    type: Number,
    default: 50
  },
  
  contentZIndex: {
    type: Number,
    default: 100
  },
  
  makeRelative: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue', 'open', 'close'])

// Local state
const dropdownRef = ref(null)
const isAnimating = ref(false)

// Methods
const toggleDropdown = () => {
  const newValue = !props.modelValue
  emit('update:modelValue', newValue)
}

const closeDropdown = () => {
  if (props.modelValue) {
    emit('update:modelValue', false)
  }
}

const handleOverlayClick = () => {
  if (props.closeOnOverlayClick) {
    closeDropdown()
  }
}

// Emit open/close events when modelValue changes
watch(() => props.modelValue, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    emit(newVal ? 'open' : 'close')
  }
})

// Expose methods for parent components
defineExpose({
  close: closeDropdown,
  toggle: toggleDropdown
})
</script>